import { useMemo } from 'react';
import { ColDef } from 'ag-grid-community';
import { useTranslation } from 'react-i18next';
import { ReceptionListItem } from '../models/Reception';
import { CustomColDef } from '@shared/components/ui/AgGridTable';
import { ReceptionStatusBadge } from '../components/display/ReceptionStatusBadge';
import { DropdownActions } from '../components/interaction/DropdownActions';

// Tipos para los handlers
interface UseAgGridColumnsProps {
  onEdit?: (reception: ReceptionListItem) => void;
  onDelete?: (reception: ReceptionListItem) => void;
}

/**
 * Hook para manejar las columnas de AG Grid de Recepciones
 */
export const useAgGridColumns = ({ onEdit, onDelete }: UseAgGridColumnsProps) => {
  const { t } = useTranslation('receptionsModule');

  // Formatear fecha
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Definiciones de columnas para AG Grid
  const allColumnDefs: ColDef[] = useMemo(() => {
    const baseColumns: CustomColDef[] = [
      // COLUMNA: Código
      {
        field: 'code',
        headerName: t('table.code'),
        minWidth: 120,
        sortable: true,
        cellRenderer: (params: any) => {
          const reception = params.data as ReceptionListItem;
          return <span className="text-sm font-medium text-title">{reception.code}</span>;
        },
      },

      // COLUMNA: Transportista
      {
        field: 'carrier',
        headerName: t('table.carrier'),
        minWidth: 120,
        sortable: true,
        cellRenderer: (params: any) => {
          const reception = params.data as ReceptionListItem;
          return <span className="text-sm text-foreground">{reception.carrier}</span>;
        },
      },

      // COLUMNA: Matrícula
      {
        field: 'vehicle_plate',
        headerName: t('table.vehicle_plate'),
        minWidth: 100,
        sortable: false,
        cellRenderer: (params: any) => {
          const reception = params.data as ReceptionListItem;
          return <span className="text-sm text-foreground">{reception.vehicle_plate}</span>;
        },
      },

      // COLUMNA: Conductor
      {
        field: 'driver_name',
        headerName: t('table.driver_name'),
        minWidth: 140,
        sortable: false,
        cellRenderer: (params: any) => {
          const reception = params.data as ReceptionListItem;
          return <span className="text-sm text-foreground">{reception.driver_name}</span>;
        },
      },

      // COLUMNA: Estado
      {
        field: 'status',
        headerName: t('table.status'),
        minWidth: 130,
        sortable: true,
        cellRenderer: (params: any) => {
          const reception = params.data as ReceptionListItem;
          return <ReceptionStatusBadge status={reception.status} />;
        },
      },

      // COLUMNA: Paquetes (Total / Pendientes / Identificados)
      {
        field: 'packages',
        headerName: t('table.packages'),
        minWidth: 180,
        sortable: false,
        cellRenderer: (params: any) => {
          const reception = params.data as ReceptionListItem;
          return (
            <div className="flex items-center gap-3 text-sm">
              <div className="flex items-center gap-1">
                <span className="text-muted-foreground">Total:</span>
                <span className="font-medium text-foreground">{reception.total_qty}</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-muted-foreground">Pend:</span>
                <span className="font-medium text-warning">{reception.pending_qty}</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-muted-foreground">Id:</span>
                <span className="font-medium text-success">{reception.identified_qty}</span>
              </div>
            </div>
          );
        },
      },

      // COLUMNA: Fecha
      {
        field: 'created_at',
        headerName: t('table.created_at'),
        minWidth: 140,
        sortable: true,
        cellRenderer: (params: any) => {
          const reception = params.data as ReceptionListItem;
          return <span className="text-sm text-muted-foreground">{formatDate(reception.created_at)}</span>;
        },
      },

      // COLUMNA: Acciones
      {
        headerName: '',
        field: 'actions',
        width: 60,
        minWidth: 60,
        maxWidth: 60,
        resizable: false,
        pinned: 'right',
        lockPosition: 'right',
        suppressMovable: true,
        suppressSizeToFit: true,
        suppressAutoSize: true,
        cellRenderer: (params: any) => {
          const reception = params.data as ReceptionListItem;
          return (
            <div className="flex justify-end w-full">
              <DropdownActions reception={reception} onEdit={onEdit} onDelete={onDelete} />
            </div>
          );
        },
      },
    ];

    return baseColumns;
  }, [t, onEdit, onDelete]);

  const filteredColumnDefs: ColDef[] = allColumnDefs;

  return {
    allColumnDefs,
    filteredColumnDefs,
  };
};
